import { Errors, SerializableError } from "--root/Errors"
import { CurrentAuthToken } from "--client/common/TypedStore"
import { SurfaceBody } from "--floponents-web/atoms/Surface";
import { SurfaceActionBar } from "--floponents-web/atoms/Surface";
import { SurfaceHeader } from "--floponents-web/atoms/Surface";
import { Surface } from "--floponents-web/atoms/Surface";
import { Button } from "--floponents-web/atoms/Button";
import { Badge } from "--floponents-web/atoms/Badge";
import { Body1, Body2, Heading5 } from "--components/typography";
import * as React from "react"
import IcoError from "@floji-internal/mui-icons/Error"
import styled, { DefaultTheme, ThemeProvider, useTheme } from "styled-components"
import { StringsErrors } from "--root/strings/en.errors";
import { Strings } from "--root/strings/en";
import { useIsOnline } from "../hooks/useOnline"
import { flojiLight } from "--components/theme"

// Default error renderer
const StyledError = styled.div`    
    top: 0; right: 0; bottom: 0; left: 0;
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: center;

    &>div {
        pointer-events: initial;
        z-index: ${props => props.theme.zIndex.modal + 1};
        width: 540px;
        max-width: 90vw;
    }
`;
const StyledHeading = styled(Heading5)`
    display: flex;
    align-items: center;
    &>svg {
        margin-right: 0.25em;
    }
`;
const StyledBody = styled(SurfaceBody)`
    &>*:not(:last-child) {
        margin-bottom: 0.5em;
    }
`;

export type ErrorMessageProps = {
    error: any
    errorActions?: React.ReactNode
    allowRefresh?: boolean | "auto"
}
const getErrorMessage = (error: any) => {
    type tError = {
        title: String
        body: String
        statusCode?: number
        errTitle?: string
        errBody?: string
    };

    const ret: tError = {
        title: Strings.errors.genericTitle,
        body: Strings.errors.genericBody
    }

    if (error instanceof SerializableError) {
        if (error.type in StringsErrors) {
            // Add some friendly strings from the lang file if present
            ret.title = StringsErrors[error.type]!.title;
            ret.body = StringsErrors[error.type]!.body;
            ret.statusCode = error.httpStatus;
            ret.errTitle = error.name;
            ret.errBody = error.message;
        } else {
            ret.statusCode = error.httpStatus;
            ret.title = error.name;
            ret.body = error.message;
        }
    } else {
        ret.errBody = error.toString();
    }
    return ret;
}

const CountdownTimer: React.FC<{ at: number, onComplete?: () => void }> = props => {
    const [_, setV] = React.useState(0);
    //attempt at offering some precision in the timer
    React.useEffect(() => {
        let cxl = false;
        (async () => {
            while (!cxl) {
                setV(v => v + 1);
                const now = Date.now();
                const milliseconds = 1000 - (now % 1000);
                await new Promise(R => setTimeout(R, milliseconds));
            }
        })();
        return () => { cxl = true };
    }, [props.at]);
    //fire at previse moment of expiry
    //or as close as event loop will permit
    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setV(v => v + 1);
        }, props.at - Date.now());
        return (() => clearTimeout(timeout));
    }, [props.at])
    const remainingS = Math.max(0, Math.floor((props.at - Date.now()) / 1000));
    React.useEffect(() => {
        if (remainingS == 0)
            props.onComplete?.();
    });
    return <>
        {remainingS}s
    </>
}

export const ErrorMessage: React.FC<ErrorMessageProps> = props => {
    const { error, allowRefresh, errorActions } = props;
    const theme = useTheme();
    const online = useIsOnline();
    const refreshAt = React.useMemo(() => {
        const maybeAsNumber = (val: string | null) => {
            if (!val) return null;
            const ret = parseInt(val, 10);
            if (ret.toString() === val) return ret;
            return null;
        }

        if (props.allowRefresh === "auto") {
            let intervalSize = 100;
            const lastIntervalSize = maybeAsNumber(sessionStorage.getItem("$$lastErrorInterval$$"));
            const lastIntervalAt = maybeAsNumber(sessionStorage.getItem("$$lastErrorIntervalAt$$"));
            if (lastIntervalAt && lastIntervalSize && Date.now() - lastIntervalAt < 60000) {
                intervalSize = lastIntervalSize * 2;
            }
            intervalSize = Math.min(intervalSize, 120 * 1000);
            return { at: Date.now() + intervalSize, intervalSize }
        }
        return undefined;
    }, []);
    const { title, body, statusCode, errTitle, errBody } = getErrorMessage(error);

    const onRefresh = React.useCallback(() => {
        if (refreshAt) {
            sessionStorage.setItem("$$lastErrorInterval$$", refreshAt.intervalSize.toString());
            sessionStorage.setItem("$$lastErrorIntervalAt$$", Date.now().toString());
        }
        window.location.reload();
    }, []);

    return <StyledError>
        <Surface elevation={2}>
            <SurfaceHeader>
                <StyledHeading>
                    <IcoError color={theme.palette.danger.main} />
                    {title}
                </StyledHeading>
            </SurfaceHeader>
            <StyledBody>
                <Body1>{body}</Body1>
                {errTitle && <Body2 emphasis>{statusCode && `${statusCode}: `}{errTitle}</Body2>}
                {errBody && <Body2 margin>{errBody}</Body2>}
                {!online && <Body2><Badge color={"danger"} /> {Strings.errors.offline}</Body2>}
            </StyledBody>
            {(allowRefresh || errorActions) && <SurfaceActionBar>
                {allowRefresh && <Button onClick={onRefresh}>
                    {Strings.errors.refresh}
                    {refreshAt
                        ? <>
                            &nbsp;
                            <CountdownTimer at={refreshAt.at} onComplete={onRefresh} />
                        </>
                        : null
                    }
                </Button>}
                {errorActions}
            </SurfaceActionBar>}
        </Surface>
    </StyledError>
}


// Error Boundary
type ErrorBoundaryProps = {
    fallback?: React.FC<ErrorMessageProps>,
    onError?: (e: any) => void
    errorActions?: React.ReactNode
    allowRefresh?: boolean | "auto"
}
type ErrorBoundaryState = {
    error: any
}
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState>{
    state = {
        error: undefined
    }

    static getDerivedStateFromError(error: any) {
        return { error }
    }

    componentDidCatch(error: any, errInfo: any) {
        if (this.props.onError) {
            this.props.onError(error)
        }

        if (error instanceof Errors.InvalidAuthTokenError || error instanceof Errors.ExpiredTokenError || error instanceof Errors.WorkspaceError) {
            CurrentAuthToken.delete().then(() => {
                console.debug("boundary reset");
                this.setState({ error: undefined })
            });
        }

        console.error("boundary error", error, errInfo);
    }

    render() {
        const err = this.state.error;
        const theme = known_theme || flojiLight;
        if (err) {
            return <ThemeProvider theme={theme}>
                {this.props.fallback ?
                    <this.props.fallback error={err} /> :
                    <ErrorMessage
                        error={err}
                        allowRefresh={this.props.allowRefresh}
                        errorActions={this.props.errorActions} />
                }
            </ThemeProvider>
        }

        return <>
            <ErrorBoundaryThemeSetter />
            {this.props.children}
        </>
    }
}

// Get the theme safely
let known_theme: DefaultTheme | undefined = undefined
const ErrorBoundaryThemeSetter: React.FC = () => {
    try {
        known_theme = useTheme();
    } catch (e) {
        //ignore as not in theme provider
    }
    return null;
}


class ErrorBoundaryFallbackRetry extends React.Component<{ fallback: React.FC<{ clear: () => void, error: any }> }> {
    state = { error: undefined as any }

    static getDerivedStateFromError(error: any) {
        // Update state so the next render will show the fallback UI.
        return { error };
    }

    render() {
        if (this.state.error) {
            // You can render any custom fallback UI
            return <this.props.fallback error={this.state.error} clear={() => {
                this.setState({ error: undefined })
            }} />
        }
        return this.props.children;
    }
}

export const RetryErrorBoundary: React.FC<{ Fallback: React.FC<{ error: any }>, maxAttempts?: number, retryTime?: number }> = props => {
    const { retryTime = 3000, maxAttempts = 3 } = props
    const key = React.useRef(1);
    const Fallback: React.FC<{ clear: () => void, error: any }> = (_props) => {
        React.useEffect(() => {
            if (key.current > maxAttempts) return
            const timer = setTimeout(() => {
                _props.clear();

            }, retryTime);
            return () => clearTimeout(timer);
        }, [])
        return <><props.Fallback error={_props.error} /></>
    }

    return <ErrorBoundaryFallbackRetry fallback={Fallback}>
        {props.children}
    </ErrorBoundaryFallbackRetry>
}